/**
 * Parses message text for @mentions and returns structured data
 * @param {string} messageText - The raw message text
 * @param {Array} messages - Array of current messages to match nicknames and colors
 * @returns {Array} Array of text parts and mention objects
 */
export function parseMentions(messageText, messages) {
  if (!messageText) return [{ type: 'text', content: messageText }];

  // Create a map of nicknames to user info from current messages
  const userMap = new Map();
  messages.forEach(message => {
    if (message.nickname && message.userId !== 'self') {
      userMap.set(message.nickname.toLowerCase(), {
        nickname: message.nickname,
        color: message.color,
        userId: message.userId,
        age: message.age,
        pronouns: message.pronouns
      });
    }
  });

  // Find all @ symbols and try to match nicknames starting from each one
  const parts = [];
  let currentIndex = 0;

  while (currentIndex < messageText.length) {
    const atIndex = messageText.indexOf('@', currentIndex);
    
    if (atIndex === -1) {
      // No more @ symbols, add remaining text
      if (currentIndex < messageText.length) {
        parts.push({
          type: 'text',
          content: messageText.slice(currentIndex)
        });
      }
      break;
    }

    // Add text before this @ symbol
    if (atIndex > currentIndex) {
      parts.push({
        type: 'text',
        content: messageText.slice(currentIndex, atIndex)
      });
    }

    // Try to find a matching nickname starting after the @
    const textAfterAt = messageText.slice(atIndex + 1);
    let bestMatch = null;
    let bestMatchLength = 0;

    // Check all known nicknames to see if any match at this position
    for (const [nicknameKey, userInfo] of userMap.entries()) {
      const nickname = userInfo.nickname;
      
      // Check if the text after @ starts with this nickname
      if (textAfterAt.toLowerCase().startsWith(nickname.toLowerCase())) {
        // Make sure this is a complete word boundary (next char should be space, end, or punctuation)
        const nextCharIndex = atIndex + 1 + nickname.length;
        const nextChar = messageText[nextCharIndex];
        
        if (!nextChar || /[\s.,!?;:]/.test(nextChar)) {
          // This is a valid match, keep track of the longest one
          if (nickname.length > bestMatchLength) {
            bestMatch = userInfo;
            bestMatchLength = nickname.length;
          }
        }
      }
    }

    if (bestMatch) {
      // Found a valid mention
      const mentionText = '@' + bestMatch.nickname;
      parts.push({
        type: 'mention',
        content: mentionText,
        nickname: bestMatch.nickname,
        color: bestMatch.color,
        userId: bestMatch.userId,
        originalText: mentionText
      });
      currentIndex = atIndex + 1 + bestMatchLength;
    } else {
      // No valid mention found, treat @ as regular text
      parts.push({
        type: 'text',
        content: '@'
      });
      currentIndex = atIndex + 1;
    }
  }


  // If no mentions found, return the original text
  if (parts.length === 0) {
    parts.push({
      type: 'text',
      content: messageText
    });
  }

  return parts;
}

/**
 * Extracts all mentioned user IDs from a message
 * @param {string} messageText - The raw message text  
 * @param {Array} messages - Array of current messages to validate nicknames
 * @returns {Array} Array of mentioned user IDs
 */
export function extractMentionedUsers(messageText, messages) {
  const parts = parseMentions(messageText, messages);
  return parts
    .filter(part => part.type === 'mention')
    .map(part => part.userId);
}